// Tank fires (DESIGN §15): summer storm warning → lightning strike → the player chooses how to fight it.
// Historical Oil Region practice: smother with steam from the boilers, or fire a cannon into the tank low down
// to drain the burning oil into a pit before it boils over onto the neighbours.
import { wellsOnField } from './indexes.js';
import { chance, nextFloat } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { eqDef, fieldDef, stat, eraDollars } from './defs.js';
import { activeRigCrew } from './crew.js';
import { queueDecision, popDecision } from './security.js';
import { spend as spendTokens, balance as tokenBalance } from '../economy/tokens.js';
import { clamp, notify, log, spend, nextId, fmt$ } from './util.js';
import { ERA_BY_ID } from '../data/eras.js';

const STORM_MONTHS = new Set([5, 6, 7, 8, 9]);
const WARN_CHANCE = 1 / 45;           // per field per summer day while it has tanks
const WARN_COOLDOWN = 45;             // polish: min days between storm warnings for the same field
const WARN_GAP_ALL = 7;               // polish: min days between storm warnings across all fields (stops toast spam)
const key = (fieldId) => `tank_fire:${fieldId}`;
const eraNum = (state) => ERA_BY_ID[state.eraId]?.num ?? 1;
const brigadeTokens = (state) => 5 + 2 * eraNum(state);

// Daily: maybe post a "thunderheads" warning for a field with tanks. Responding is the normal respondWarning action.
export function maybeStormWarning(state, f, tanks) {
  if (!tanks.length || !STORM_MONTHS.has(dayToDate(state.day).month)) return;
  const s = state.safety;
  if (!s) return;
  const k = key(f.id);
  if (Object.values(s.warnings).some((w) => w.key === k) || (s.calm[k] ?? -1) >= state.day) return;
  if (!chance(state, WARN_CHANCE)) return;
  // polish: rate-limit per field and globally (rolled after chance() so the RNG stream is unchanged).
  const last = (s.stormLast ??= {});
  if (state.day - (last[k] ?? -1e9) < WARN_COOLDOWN || state.day - (last._any ?? -1e9) < WARN_GAP_ALL) return;
  last[k] = last._any = state.day;
  const id = nextId(state, 'sw');
  const name = fieldDef(f.id)?.name ?? f.id;
  const cost = Math.round(eraDollars(state, 4, 1860) * 100) / 100;
  s.warnings[id] = {
    id, key: k, type: 'tank_fire', rigUid: null, wellId: null, fieldId: f.id, day: state.day, expires: state.day + 4, cost, responded: false,
    text: `${name}: thunderheads building over the tanks`,
  };
  notify(state, 'warning', { warningId: id, incidentType: 'tank_fire', fieldId: f.id, wellId: null,
    text: `${name}: thunderheads over the tanks. Draw them down and post a watch for ${fmt$(cost >= 100 ? Math.round(cost) : cost)}?`, level: 'warn' });
}

// Multiplier on the lightning roll: an active unanswered warning or an ignored one makes it likelier; a response calms it.
export function stormMult(state, fieldId) {
  const s = state.safety;
  if (!s) return 1;
  const k = key(fieldId);
  // Base wooden-tank rate is ~0.0002/day; risk is concentrated in storms so warnings mean something:
  // an ignored warning ≈ 8% chance of a fire, a heeded one ≈ none, and calm weather is safer than before.
  if ((s.calm[k] ?? -1) >= state.day) return 0.2;
  if (Object.values(s.warnings).some((w) => w.key === k)) return 40;
  if ((s.ignored[k] ?? -1) >= state.day) return 25;
  return STORM_MONTHS.has(dayToDate(state.day).month) ? 0.5 : 0.1;
}

function steamAvailable(state, fieldId) {
  return Object.values(state.rigs).some((r) => {
    if (r.at?.fieldId !== fieldId || !r.engineUid) return false;
    const e = state.items[r.engineUid];
    return !!e && /steam/.test(e.defId);
  });
}
function fieldCrew(state, fieldId) {
  return Object.values(state.rigs).filter((r) => r.at?.fieldId === fieldId).flatMap((r) => activeRigCrew(state, r));
}
function wellForVisual(state, fieldId) {
  const ws = wellsOnField(state, fieldId);
  return (ws.find((w) => w.status === 'PRODUCING') ?? ws[0])?.id ?? null;
}

// Lightning has struck `tank` (an ItemInstance) at field state `f`. Opens a decision; nothing is lost until it resolves.
export function startTankFire(state, f, tank) {
  const d = eqDef(tank.defId);
  const s = f.storage;
  const share = s.capacityBbl > 0 ? Math.max(0, s.bbl - s.barrels) * stat(d, 'capacityBbl', 0) / s.capacityBbl : 0;
  const name = fieldDef(f.id)?.name ?? f.id;
  const id = nextId(state, 'tf');
  const wellId = wellForVisual(state, f.id);
  const crew = fieldCrew(state, f.id);
  const skill = crew.length ? crew.reduce((a, c) => a + (c.skill ?? 40), 0) / crew.length : 30;
  const steamOdds = clamp(0.45 + skill / 400, 0.4, 0.75);
  const cannonCost = Math.round(eraDollars(state, 40, 1860));
  const steamOk = steamAvailable(state, f.id);
  const tk = brigadeTokens(state);
  f.tankFire = { id, tankUid: tank.uid, day: state.day, bbl: share, steamOdds, cannonCost, tokens: tk };
  queueDecision(state, {
    kind: 'tankFire', id, fieldId: f.id, wellId, day: state.day,
    title: 'Tank Fire!',
    text: `Lightning struck your ${d.name} at ${name}. About ${Math.round(share).toLocaleString('en-US')} bbl are burning. How do you fight it?`,
    options: [
      { choice: 'steam', label: 'Smother it with steam from the boilers', odds: steamOk ? steamOdds : undefined,
        desc: steamOk ? 'Run steam lines onto the roof. If it works the tank survives with most of its oil; if not, it burns out.' : 'Needs a rig with a steam engine on this field.',
        disabled: !steamOk },
      { choice: 'cannon', label: `Fire the cannon (${fmt$(cannonCost)})`, cost: cannonCost,
        desc: 'Shoot a hole low in the tank and drain the burning oil into a pit. The tank is lost, about half the oil is saved, and it cannot boil over.' },
      { choice: 'brigade', label: `Hire a fire brigade (${tk} ◈)`, tokens: tk,
        desc: 'Experienced fire-fighters arrive at once. The tank is saved with nearly all its oil.', disabled: tokenBalance(state) < tk },
      { choice: 'burn', label: 'Let it burn out', tone: 'danger',
        desc: 'Keep the men back and protect the other tanks. The tank and its oil are lost, and it may boil over onto a neighbour.' },
    ],
  });
  notify(state, 'accident', { incidentType: 'tank_fire', fieldId: f.id, wellId, rigUid: null, pause: true, fatal: false,
    text: `Lightning struck your ${d.name} at ${name}! The tank is ablaze.`, level: 'warn' });
}

function loseTank(state, f, tankUid) {
  delete state.items[tankUid];
}

export function resolveTankFireImpl(state, decisionId, choice) {
  const f = Object.values(state.fields).find((x) => x.tankFire?.id === decisionId);
  if (!f) { popDecision(state, decisionId); return { ok: false, msg: 'The fire is already out.' }; }
  const tf = f.tankFire;
  const tank = state.items[tf.tankUid];
  const s = f.storage;
  const name = fieldDef(f.id)?.name ?? f.id;
  const tankName = tank ? eqDef(tank.defId)?.name ?? 'tank' : 'tank';
  let lostBbl = tf.bbl, tankLost = true, msg;
  const burn = () => {
    // boilover: 25% chance the burning oil spreads to another tank on the field
    const others = Object.values(state.items).filter((t) => t.uid !== tf.tankUid && t.loc?.type === 'field' && t.loc.id === f.id && eqDef(t.defId)?.cat === 'storage');
    if (others.length && chance(state, 0.25)) {
      const o = others[Math.floor(nextFloat(state) * others.length)];
      const od = eqDef(o.defId);
      const oShare = s.capacityBbl > 0 ? Math.max(0, s.bbl - s.barrels) * stat(od, 'capacityBbl', 0) / s.capacityBbl : 0;
      lostBbl += oShare;
      loseTank(state, f, o.uid);
      state.company.reputation = Math.max(0, state.company.reputation - 2);
      return ` It boiled over and took your ${od.name} with it.`;
    }
    return '';
  };
  switch (choice) {
    case 'steam': {
      if (!steamAvailable(state, f.id)) return { ok: false, msg: 'No steam engine on this field.' };
      if (chance(state, tf.steamOdds)) { lostBbl = tf.bbl * 0.25; tankLost = false; msg = `The steam smothered it. The ${tankName} is saved; about ${Math.round(lostBbl)} bbl burned.`; }
      else msg = `The steam couldn't hold it and the ${tankName} burned out.` + burn();
      break;
    }
    case 'cannon': {
      if (!spend(state, tf.cannonCost, 'other')) return { ok: false, msg: `The cannon crew wants ${fmt$(tf.cannonCost)}.` };
      lostBbl = tf.bbl * 0.5;
      msg = `The cannon holed the ${tankName} low down and the burning oil drained into a pit. About ${Math.round(tf.bbl - lostBbl)} bbl saved.`;
      break;
    }
    case 'brigade': {
      const r = spendTokens(state, tf.tokens, 'tank_fire_brigade', f.id);
      if (!r.ok) return r;
      lostBbl = tf.bbl * 0.05; tankLost = false;
      msg = `The fire brigade had it out within the hour. The ${tankName} is saved.`;
      break;
    }
    case 'burn':
    default:
      msg = `The ${tankName} burned out.` + burn();
  }
  s.bbl = Math.max(0, s.bbl - lostBbl);
  if (tankLost && tank) loseTank(state, f, tf.tankUid);
  s.capacityBbl = Object.values(state.items).filter((t) => t.loc?.type === 'field' && t.loc.id === f.id && eqDef(t.defId)?.cat === 'storage')
    .reduce((a, t) => a + stat(eqDef(t.defId), 'capacityBbl', 0), 0);
  f.tankFireDone = { day: state.day, untilDay: state.day + (tankLost ? 6 : 2) };
  delete f.tankFire;
  popDecision(state, decisionId);
  log(state, `${name}: ${msg}`, 'warn');
  return { ok: true, msg };
}
